import React, {useState} from "react"
import {AsyncResultModal} from "../../components/AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {Account, getBankGLAccounts, getOrgGeneralLedgerAccounts, isWalletAccount} from "../../resources/account"
import {useAccessToken} from "../../services/auth"
import {createAdjustment} from "../../resources/adjustment"
import {toCents} from "../../utilities/numbers"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {AsyncResultRequestState} from "../../types/asyncResult"
import {Result} from "neverthrow"

interface CreateAdjustmentModalProps {
	account: Account
	closeModal: () => void
	onSuccess: () => void
}

function refreshAndClose(refresh: () => void, close: () => void) {
	return function () {
		refresh()
		close()
	}
}

async function getWalletOrgGlAccounts(
	account: Account,
	accessToken: string,
	bankId: string
): Promise<Result<OkDocument<Account[]>, ErrorDocument>> {
	if (isWalletAccount(account)) {
		const orgId = account.relationships.org.data.id
		const accounts = await getOrgGeneralLedgerAccounts(accessToken, bankId, orgId)
		return accounts.map((data): OkDocument<Account[]> => ({data}))
	} else {
		return getBankGLAccounts(accessToken, bankId)
	}
}

function CounterAccountSelect({
	glAccounts,
	counterAccount,
	setCounterAccount,
}: {
	glAccounts: AsyncResultRequestState<OkDocument<Account[]>, ErrorDocument>
	counterAccount: string | null
	setCounterAccount: (account: string | null) => void
}) {
	return glAccounts.match(
		() => (
			<select
				value={counterAccount ?? "default"}
				onChange={(e) => setCounterAccount(e.target.value === "default" ? null : e.target.value)}
			>
				<option key="default" value="default">
					Default
				</option>
			</select>
		),
		(accounts) => {
			return (
				<select
					value={counterAccount ?? "default"}
					onChange={(e) => setCounterAccount(e.target.value === "default" ? null : e.target.value)}
				>
					<option key="default" value="default">
						Default
					</option>
					{accounts.data.reverse().map((account) => (
						<option key={account.id} value={account.id}>
							{account.attributes.name}
						</option>
					))}
				</select>
			)
		},
		() => null
	)
}

export function CreateAdjustmentModal({account, closeModal, onSuccess}: CreateAdjustmentModalProps) {
	const accessToken = useAccessToken()
	const [amount, setAmount] = useState("0")
	const [direction, setDirection] = useState("Debit")
	const [description, setDescription] = useState("")
	const [internalNote, setInternalNote] = useState("")
	const [state, create] = useAsyncResultIdle(createAdjustment)
	const [counterAccount, setCounterAccount] = useState<string | null>(null)

	const bankId = (account.relationships as {bank?: {data: {id: string}}}).bank?.data.id ?? ""
	const glAccounts = useAsyncResult<OkDocument<Account[]>, ErrorDocument>(
		() => getWalletOrgGlAccounts(account, accessToken, bankId),
		[account]
	)

	return (
		<AsyncResultModal
			title="Create Account Adjustment"
			close={refreshAndClose(onSuccess, closeModal)}
			buttonClassname="is-success"
			state={state}
			buttonText="Create"
			successText="Created"
			onSubmit={() =>
				create(accessToken, account.id, counterAccount, toCents(amount), direction, description, internalNote)
			}
			errorToText={(err) => err.errors[0].title}
		>
			<div className="field">
				<label className="label">Direction</label>
				<div className="control">
					<div className="select">
						<select onChange={(e) => setDirection(e.target.value)} value={direction}>
							<option value="Debit">Debit</option>
							<option value="Credit">Credit</option>
						</select>
					</div>
				</div>
			</div>
			<div className="field">
				<label className="label">Amount $</label>
				<div className="control">
					<input
						className="input"
						type="number"
						value={amount}
						step=".01"
						required
						onChange={(e) => setAmount(e.target.value)}
					/>
				</div>
			</div>
			<div className="field">
				<label className="label">Counter Account</label>
				<div className="select is-fullwidth">
					<CounterAccountSelect
						glAccounts={glAccounts}
						counterAccount={counterAccount}
						setCounterAccount={setCounterAccount}
					/>
				</div>
			</div>
			<div className="field">
				<label className="label">Description</label>
				<div className="control">
					<textarea
						className="textarea is-static has-fixed-size"
						value={description ?? ""}
						required
						onChange={(e) => setDescription(e.target.value)}
					/>
				</div>
			</div>
			<div className="field">
				<label className="label">Internal Note</label>
				<div className="control">
					<textarea
						className="textarea is-static has-fixed-size"
						value={internalNote ?? ""}
						required
						onChange={(e) => setInternalNote(e.target.value)}
					/>
				</div>
			</div>
		</AsyncResultModal>
	)
}
